// React 18: shadcn's current templates are React-19 style (ref is a plain
// prop, no forwardRef). Every wrapper forwards its ref here, or Radix's
// Slot/Presence (asChild triggers, portal overlays) can't reach the DOM
// node — focus return and exit animations break and React warns.
// Regenerate + re-apply (projects/tailwind-migration).
//
// Generated from shadcn's `sheet` (new-york; Radix Dialog from the radix-ui
// umbrella — no new dependency). Two changes on top, both from ui/dialog.jsx
// so a sheet keeps the house dialog contract (porting guide): the surface
// is `bg-card` like every dialog, and SheetContent hands focus back to
// whoever opened it (these sheets are controlled, with no trigger, so
// Radix's own close-autofocus fell to <body>). `showCloseButton={false}`
// hides the X while a request is in flight.
import * as React from "react";
import { forwardRef } from "react";
import { XIcon } from "lucide-react";
import { Dialog as SheetPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";

const Sheet = forwardRef(function Sheet({ ...props }, ref) {
  return <SheetPrimitive.Root ref={ref} data-slot="sheet" {...props} />;
});

const SheetTrigger = forwardRef(function SheetTrigger({ ...props }, ref) {
  return (
    <SheetPrimitive.Trigger ref={ref} data-slot="sheet-trigger" {...props} />
  );
});

const SheetClose = forwardRef(function SheetClose({ ...props }, ref) {
  return <SheetPrimitive.Close ref={ref} data-slot="sheet-close" {...props} />;
});

const SheetPortal = forwardRef(function SheetPortal({ ...props }, ref) {
  return (
    <SheetPrimitive.Portal ref={ref} data-slot="sheet-portal" {...props} />
  );
});

const SheetOverlay = forwardRef(function SheetOverlay(
  { className, ...props },
  ref,
) {
  return (
    <SheetPrimitive.Overlay
      ref={ref}
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
});

const SheetContent = forwardRef(function SheetContent(
  {
    className,
    children,
    side = "right",
    showCloseButton = true,
    onOpenAutoFocus,
    onCloseAutoFocus,
    ...props
  },
  ref,
) {
  // Hand-written on top of the generated component, as in ui/dialog.jsx:
  // remember whoever had focus when the sheet opened and hand it back. A
  // consumer's own handlers still run and may preventDefault.
  const openerRef = React.useRef(null);
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        data-slot="sheet-content"
        className={cn(
          "fixed z-50 flex flex-col gap-4 bg-card text-card-foreground shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500",
          side === "right" &&
            "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
          side === "left" &&
            "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
          side === "top" &&
            "inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          side === "bottom" &&
            "inset-x-0 bottom-0 h-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className,
        )}
        onOpenAutoFocus={(event) => {
          openerRef.current = document.activeElement;
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          if (event.defaultPrevented) return;
          event.preventDefault();
          openerRef.current?.focus?.();
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-secondary">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});

const SheetHeader = forwardRef(function SheetHeader(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  );
});

const SheetFooter = forwardRef(function SheetFooter(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
});

const SheetTitle = forwardRef(function SheetTitle(
  { className, ...props },
  ref,
) {
  return (
    <SheetPrimitive.Title
      ref={ref}
      data-slot="sheet-title"
      className={cn("font-semibold text-foreground", className)}
      {...props}
    />
  );
});

const SheetDescription = forwardRef(function SheetDescription(
  { className, ...props },
  ref,
) {
  return (
    <SheetPrimitive.Description
      ref={ref}
      data-slot="sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
